/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useContext, type ReactElement, useState } from 'react';

import { useOwnershipMarkerComponent } from '@jira/platform__ownership-marker/src/ui/useOwnershipMarkerComponent.tsx';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';
import useLayoutEffectSAFE from '@jira/platform__app-framework-use-layout-effect-safe';
import { type LazySuspenseProps, LazySuspense, WaitContext } from '@atlassian/react-loosely-lazy';
import { componentWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { SuspensePlaceholder } from './SuspensePlaceholder.tsx';
import { useMountTracker } from './useMountTracker.tsx';

function Hold({ name }: { name?: string }): null {
	const interactionContext = useContext(UFOInteractionContext);
	const waitContext = useContext(WaitContext);
	const nameWithUnderscores = name?.replaceAll('/', '_');

	// Runs synchronously, to allow us to identify async items
	useLayoutEffectSAFE(() => {
		// if until context exists means there is a LazyWait parent component
		// we use that value
		const shouldHold = waitContext ? !!waitContext.currentValue() : true;
		if (interactionContext != null && shouldHold) {
			return interactionContext.hold(nameWithUnderscores);
		}
	}, [interactionContext, nameWithUnderscores, waitContext]);
	return null;
}

/**
 * This is a "safe-to-use" Placeholder but it's actually expected to be used only around React-Loosely-Lazy components.
 * WARNING: Make sure we use same placeholders in a rendering tree.
 * Mixed use may cause unexpected behavior, such as rendering child component twice.
 * Ref: https://hello.atlassian.net/wiki/x/iJpOHwE
 */
function RLLPlaceholder({
	name,
	children,
	fallback,
}: {
	/**
	 * Named Placeholders will help when analysing the break-down of the page load in Performance Portal.
	 * */
	name: string;
	children?: LazySuspenseProps['children'];
	fallback?: LazySuspenseProps['fallback'];
}): ReactElement {
	// react-18: can use fallback directly
	const fb = fallback != null ? fallback : null;
	// react-18: can use children directly
	const kids = children != null ? children : null;
	const OwnershipMarker = useOwnershipMarkerComponent({ markerType: 'placeholder', name });
	const mountTracker = useMountTracker(`placeholder:${name}`);
	return (
		<OwnershipMarker>
			<LazySuspense
				fallback={
					<>
						{fb}
						<Hold name={name} />
					</>
				}
				name={name}
			>
				{kids}
				{mountTracker}
			</LazySuspense>
		</OwnershipMarker>
	);
}

const Placeholder: React.FC<
	{
		name: string;
		children?: LazySuspenseProps['children'];
		fallback?: LazySuspenseProps['fallback'];
	} & {
		/**
		 * Named Placeholders will help when analysing the break-down of the page load in Performance Portal.
		 * */
		name: string;
		children?: LazySuspenseProps['children'];
		fallback?: LazySuspenseProps['fallback'];
	}
> = componentWithCondition(
	// this condition is coupled with hydration rollout, not the fact of hydration
	// SuspensePlaceholder can be used for non-hydrating pages as well, if no "SSR content preservation" is required
	// See PlaceholderWithNativeSuspense
	() => useIsHydrateRenderMode(),
	SuspensePlaceholder,
	RLLPlaceholder,
);
export default Placeholder;

export const PlaceholderWithNativeSuspense: React.FC<{
	name: string;
	children?: LazySuspenseProps['children'];
	fallback?: LazySuspenseProps['fallback'];
}> = SuspensePlaceholder;

/**
 * Conditionally renders a PlaceholderWithNativeSuspense.
 * `condition` is expected to not change, as changing it would trigger a remount
 * For that reason it is frozen to its initial value it had
 */
export function ConditionalPlaceholderWithNativeSuspense({
	name,
	condition,
	children,
	fallback,
}: React.PropsWithChildren<{
	name: string;
	/** WARNING: This condition is frozen as changing it would cause a remount. Only use fg/expVal! */
	condition: boolean;
	fallback: React.ReactNode;
}>): React.JSX.Element {
	const [frozenCondition] = useState(condition);
	if (frozenCondition) {
		return (
			<PlaceholderWithNativeSuspense name={name} fallback={fallback}>
				{children}
			</PlaceholderWithNativeSuspense>
		);
	}
	return <>{children}</>;
}

/**
 * A `ConditionalPlaceholderWithNativeSuspense` present in the tree only with the intention of reducing
 * the blast radius of hydration mismatches in this section of it, to prevent large parts of the
 * server-rendered DOM from being removed and replaced by client-rendered content.
 *
 * Hydration boundaries are expected to be removed at a later point, once the underlying hydration errors are resolved or minimised.
 */
export const ConditionalHydrationBoundaryPlaceholder: typeof ConditionalPlaceholderWithNativeSuspense =
	ConditionalPlaceholderWithNativeSuspense;
